import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { personaBySlug, type PersonaSlug } from '@/lib/personas';
import { cn } from '@/lib/utils';
import { PersonaIcon } from './PersonaIcon';

/**
 * The nine role tracks, grouped into three columns so the home page asks
 * the reader one question (which kind of work do you do?) instead of
 * presenting nine equal-weight cards at once. Same nine tracks, same copy,
 * lower cognitive load.
 */
const GROUPS: { label: string; blurb: string; roles: PersonaSlug[] }[] = [
  {
    label: 'Engineering',
    blurb: 'You build and ship the system.',
    roles: [
      'principal-ai-engineer',
      'backend-engineer',
      'frontend-engineer',
      'data-engineer',
    ],
  },
  {
    label: 'Quality & Research',
    blurb: 'You prove it holds up.',
    roles: ['sdet-qa', 'research-engineer'],
  },
  {
    label: 'Leadership & Product',
    blurb: 'You decide what good means.',
    roles: ['eng-manager', 'cto', 'product-manager'],
  },
];

export function PersonaColumns({ className }: { className?: string }) {
  return (
    <div className={cn('grid gap-px border border-rule bg-rule md:grid-cols-3', className)}>
      {GROUPS.map((group) => (
        <div key={group.label} className="flex flex-col bg-bg p-5">
          <div className="font-sans text-sm font-semibold tracking-tight text-ink">
            {group.label}
          </div>
          <p className="mt-1 font-serif text-[0.9375rem] leading-snug text-ink-mute">
            {group.blurb}
          </p>

          <ul className="mt-4 flex flex-col">
            {group.roles.map((slug) => {
              const p = personaBySlug[slug];
              return (
                <li key={slug} className="border-t border-rule">
                  <Link
                    to={`/start/${slug}`}
                    className="group flex items-center gap-3 py-2.5 transition-colors"
                  >
                    <PersonaIcon
                      persona={slug}
                      size={15}
                      className="shrink-0 text-ink-mute transition-colors group-hover:text-ink"
                    />
                    <span className="min-w-0 flex-1 font-sans text-sm text-ink transition-colors group-hover:text-accent">
                      {p.shortName}
                    </span>
                    <ArrowRight
                      size={14}
                      strokeWidth={1.75}
                      aria-hidden="true"
                      className="shrink-0 -translate-x-1 text-ink-mute opacity-0 transition-all group-hover:translate-x-0 group-hover:opacity-100"
                    />
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </div>
  );
}

export default PersonaColumns;
